import React, { useEffect, useState } from "react";
import useAdminUserFetch from "../useAdminUserFetch";
import {
  Link,
  useHistory,
  useParams,
} from "react-router-dom/cjs/react-router-dom.min";
import ConfirmHardDelete from "./ConfirmHardDelete";
import { useAuth } from "../../../../utils/AuthContext";

function AdminUserProfileComponent() {
  const { userId } = useParams();
  const { useUserById, updateUser } = useAdminUserFetch();
  const { user: currentUser } = useAuth();
  const {
    data: user,
    isLoading,
    error,
    refetch: refetchUser,
  } = useUserById(userId);
  const [editMode, setEditMode] = useState(false);
  const [form, setForm] = useState(null);
  const [success, setSuccess] = useState("");
  const [isUpdating, setIsUpdating] = useState(false);
  const [isModal, setIsModal] = useState(false);
  const history = useHistory();

  // Check if the current user is trying to delete themselves
  const isCurrentUser = currentUser?._id === user?._id;

  useEffect(() => {
    if (user) {
      setForm({
        name: user.name || "",
        email: user.email || "",
        role: user.role || 0,
        isDeleted: user.isDeleted || 0,
      });
    }
  }, [user]);

  const handleEdit = () => {
    setEditMode(true);
    setSuccess("");
  };

  const handleCancel = () => {
    setEditMode(false);
    setSuccess("");
    setForm({
      name: user.name || "",
      email: user.email || "",
      role: user.role || 0,
      isDeleted: user.isDeleted || 0,
      address: user.address || "",
    });
  };

  const handleChange = (field) => (e) => {
    const value =
      e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsUpdating(true);
    setSuccess("");
    updateUser(
      {
        userId,
        userData: {
          ...form,
          role: Number(form.role),
          isDeleted: Number(form.isDeleted),
        },
      },
      {
        onSuccess: () => {
          setSuccess("User updated successfully!");
          setEditMode(false);
          setIsUpdating(false);
          refetchUser();
        },
        onError: (err) => {
          //   setError(err?.response?.data?.error || "Failed to update user");
          setIsUpdating(false);
        },
      }
    );
  };

  if (isLoading) {
    return <div className="py-12 text-center text-lg">Loading...</div>;
  }
  if (error) {
    return (
      <div className="py-12 text-center text-red-500 text-lg">
        {error.message || error}
      </div>
    );
  }
  if (!user) {
    return (
      <div className="py-12 text-center text-gray-500 text-lg">
        User not found
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto bg-white rounded-lg shadow-lg p-8 mt-8">
      <div className="flex items-center gap-6 mb-6">
        <div className="flex-shrink-0 h-20 w-20 rounded-full bg-gray-200 flex items-center justify-center text-3xl font-bold text-blue-600">
          {user.name?.charAt(0).toUpperCase()}
        </div>
        <div>
          <div className="text-2xl font-semibold text-gray-900 mb-1">
            {user.name}
          </div>
          <div className="flex gap-2 mb-1 flex-wrap">
            <span
              className={`px-2 py-1 text-xs font-semibold rounded-full ${
                user.role === 1
                  ? "bg-purple-100 text-purple-800"
                  : "bg-blue-100 text-blue-800"
              }`}
            >
              {user.role === 1 ? "Admin" : "User"}
            </span>
            <span
              className={`px-2 py-1 text-xs font-semibold rounded-full ${
                user.isDeleted === 0
                  ? "bg-green-100 text-green-800"
                  : "bg-red-100 text-red-800"
              }`}
            >
              {user.isDeleted === 0 ? "Active" : "Deleted"}
            </span>
            {!user.isVerified && (
              <span className="px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
                Not Verified
              </span>
            )}
            {user.canReceiveNotifications && (
              <span className="px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                Can Receive Notifications
              </span>
            )}
          </div>
          <div className="text-sm text-gray-500">
            Joined:{" "}
            {user.createdAt
              ? new Date(user.createdAt).toLocaleDateString()
              : "-"}
          </div>
          <div className="text-xs text-gray-400">
            Last Updated:{" "}
            {user.updatedAt
              ? new Date(user.updatedAt).toLocaleDateString()
              : "-"}
          </div>
        </div>
      </div>
      {success && <div className="mb-4 text-green-600 text-sm">{success}</div>}
      {isCurrentUser && (
        <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
          <div className="flex items-center">
            <svg
              className="w-5 h-5 text-yellow-600 mr-2"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
            <span className="text-yellow-800 text-sm font-medium">
              You are viewing your own profile. You cannot delete your own
              account for security reasons.
            </span>
          </div>
        </div>
      )}
      {editMode ? (
        <form onSubmit={handleSubmit} className="space-y-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Name
            </label>
            <input
              type="text"
              value={form.name}
              onChange={handleChange("name")}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email
            </label>
            <input
              type="email"
              value={form.email}
              onChange={handleChange("email")}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Role
            </label>
            <select
              value={form.role}
              onChange={handleChange("role")}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value={0}>User</option>
              <option value={1}>Admin</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <select
              value={form.isDeleted}
              onChange={handleChange("isDeleted")}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value={0}>Active</option>
              <option value={1}>Deleted</option>
            </select>
          </div>
          <div className="flex gap-4 mt-4">
            <button
              type="submit"
              disabled={isUpdating}
              className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isUpdating ? "Saving..." : "Save"}
            </button>
            <button
              type="button"
              onClick={handleCancel}
              className="bg-gray-300 text-gray-700 px-6 py-2 rounded-md hover:bg-gray-400 transition-colors"
            >
              Cancel
            </button>
          </div>
        </form>
      ) : (
        <>
          <div className="mb-4">
            <div className="text-gray-700 font-medium mb-1">Contact Info</div>
            <div className="text-gray-900">
              Email: <span className="font-mono">{user.email}</span>
            </div>
            <div className="text-gray-900">
              Phone: <span className="font-mono">{user.phoneNo}</span>
            </div>
          </div>
          <div className="mb-4">
            <div className="text-gray-700 font-medium mb-1">Address</div>
            <div className="text-gray-900">{user.address || "-"}</div>
          </div>
          {/* {user.addresses && user.addresses.length > 0 && (
            <div className="mb-4">
              <div className="text-gray-700 font-medium mb-1">Addresses</div>
              <ul className="list-disc pl-6">
                {user.addresses.map((addr, idx) => (
                  <li key={addr._id || idx} className="text-gray-900">
                    {addr.street || ""} {addr.city || ""} {addr.state || ""}{" "}
                    {addr.zip || ""}
                  </li>
                ))}
              </ul>
            </div>
          )} */}
          <div className="mb-4">
            <div className="text-gray-700 font-medium mb-1">
              Purchase History
            </div>
            {/* {user.history && user.history.length > 0 ? (
              <ul className="list-disc pl-6">
                {user.history.map((order, idx) => (
                  <li key={order._id || idx} className="text-gray-900">
                    <Link
                      to={`/admin/orders/${order._id}`}
                      className="text-blue-600 hover:underline"
                    >
                      Order #{order._id || idx}
                    </Link>
                    {order.createdAt && (
                      <span className="ml-2 text-xs text-gray-500">
                        {new Date(order.createdAt).toLocaleDateString()}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            ) : (
              <div className="text-gray-500 text-sm">
                No purchase history found.
              </div>
            )} */}
          </div>
          {/* <div className="mb-4">
            <div className="text-gray-700 font-medium mb-1">FCM Tokens</div>
            {user.fcmTokens && user.fcmTokens.length > 0 ? (
              <ul className="list-disc pl-6">
                {user.fcmTokens.map((token, idx) => (
                  <li key={idx} className="text-gray-900 break-all">
                    {token}
                  </li>
                ))}
              </ul>
            ) : (
              <div className="text-gray-500 text-sm">No FCM tokens found.</div>
            )}
          </div> */}
          <div className="flex gap-4 mt-6">
            <button
              onClick={handleEdit}
              className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 transition-colors"
            >
              Edit
            </button>
            {!isCurrentUser ? (
              <button
                onClick={() => setIsModal(true)}
                className="bg-red-600 text-white px-6 py-2 rounded-md hover:bg-red-700 transition-colors"
              >
                Hard Delete
              </button>
            ) : (
              <button
                disabled
                className="bg-gray-400 text-white px-6 py-2 rounded-md cursor-not-allowed opacity-50"
                title="You cannot delete your own account"
              >
                Hard Delete (Not Allowed)
              </button>
            )}
          </div>
        </>
      )}
      <ConfirmHardDelete
        open={isModal}
        onClose={() => setIsModal(false)}
        userId={user._id}
        onDeleted={() => {
          setIsModal(false);
          history.push("/admin/users");
        }}
      />
    </div>
  );
}

export default AdminUserProfileComponent;
